import { useState } from 'react';
import { toast } from 'react-toastify';
import { stanNotify } from '@/lib';
import { stanAnalytics } from '@/lib/stanAnalytics';
import { useUser } from '@/queries/useUser';
import { useModal } from '@/hooks/useModal';
import { useDeleteFlow, useDuplicateFlow, useUpdateFlowStatus } from '../queries';

export function useFlowActions() {
    const { user } = useUser();

    const { isOpen: isEditModalOpen, open: openEditModal, close: closeEditModal } = useModal();
    const { isOpen: isDeleteModalOpen, open: openDeleteModal, close: closeDeleteModal } = useModal();
    const [deleteFlowData, setDeleteFlowData] = useState<{
        flowId: number | null;
        flowName: string;
    }>({
        flowId: null,
        flowName: '',
    });

    // Use mutations from queries folder
    const updateFlowStatusMutation = useUpdateFlowStatus();
    const duplicateFlowMutation = useDuplicateFlow();
    const deleteFlowMutation = useDeleteFlow();

    const handlePublishFlow = (flowId: number) => {
        updateFlowStatusMutation.mutate(
            { flowId, status: 'published' },
            {
                onSuccess: () => {
                    stanNotify({
                        type: 'success',
                        title: 'Flow Published',
                        text: 'Your flow will now start sending emails.',
                    });
                    stanAnalytics('flow-updated', {
                        meta: { user_id: user?.user_id, username: user?.username },
                        props: { flow_id: flowId },
                    });
                },
                onError: () => {
                    stanNotify({
                        type: 'error',
                        title: 'Something went wrong',
                        text: 'Please contact us at friends@stanwith.me',
                    });
                },
            }
        );
    };

    const handleUnpublishFlow = (flowId: number) => {
        updateFlowStatusMutation.mutate(
            { flowId, status: 'draft' },
            {
                onSuccess: () => {
                    stanNotify({
                        type: 'success',
                        title: 'Flow Unpublished',
                        text: 'Your flow will no longer send emails.',
                    });
                    stanAnalytics('flow-updated', {
                        meta: { user_id: user?.user_id, username: user?.username },
                        props: { flow_id: flowId },
                    });
                },
                onError: () => {
                    stanNotify({
                        type: 'error',
                        title: 'Something went wrong',
                        text: 'Please contact us at friends@stanwith.me',
                    });
                },
            }
        );
    };

    const handleDuplicateFlow = (flowId: number) => {
        // Show loading toast immediately
        stanNotify({
            type: 'info',
            title: 'Duplicating flow...',
            duration: 10000, // Will be dismissed when success/error shows
        });

        duplicateFlowMutation.mutate(flowId, {
            onSuccess: data => {
                // Dismiss the loading toast
                toast.dismiss();

                // 1 = EVENT_TRIGGERED, 2 = USER_TRIGGERED
                const isBroadcast = data.type === 2;

                stanNotify({
                    type: 'success',
                    title: 'Done!',
                    text: isBroadcast ? 'Your broadcast has been duplicated.' : 'Your flow has been duplicated.',
                });

                stanAnalytics('flow-duplicated', {
                    meta: { user_id: user?.user_id, username: user?.username },
                    props: { flow_id: data.flow_id },
                });
            },
            onError: () => {
                // Dismiss the loading toast
                toast.dismiss();

                stanNotify({
                    type: 'error',
                    title: 'Something went wrong',
                    text: 'Please contact us at friends@stanwith.me',
                });
            },
        });
    };

    const handleOpenDeleteModal = (flowId: number, flowName: string) => {
        setDeleteFlowData({ flowId, flowName });
        openDeleteModal();
    };

    const handleCloseDeleteModal = () => {
        closeDeleteModal();

        // Wait before clearing flowId and flowName, for modal animation to finish
        setTimeout(() => {
            setDeleteFlowData({ flowId: null, flowName: '' });
        }, 300);
    };

    const confirmDelete = () => {
        if (deleteFlowData.flowId) {
            const flowId = deleteFlowData.flowId;

            handleCloseDeleteModal();

            // Delete the flow - motion will handle the exit animation
            deleteFlowMutation.mutate(flowId, {
                onSuccess: () => {
                    stanNotify({
                        type: 'success',
                        title: 'Flow Deleted',
                        text: 'Your flow has been permanently deleted.',
                    });
                },
                onError: () => {
                    stanNotify({
                        type: 'error',
                        title: 'Something went wrong',
                        text: 'Please contact us at friends@stanwith.me',
                    });
                },
            });
        }
    };

    return {
        isEditModalOpen,
        openEditModal,
        closeEditModal,
        handlePublishFlow,
        handleUnpublishFlow,
        handleDuplicateFlow,
        openDeleteModal: handleOpenDeleteModal,
        closeDeleteModal: handleCloseDeleteModal,
        confirmDelete,
        deleteModalState: {
            isOpen: isDeleteModalOpen,
            flowId: deleteFlowData.flowId,
            flowName: deleteFlowData.flowName,
        },
        isPublishing: updateFlowStatusMutation.isPending,
        isUnpublishing: updateFlowStatusMutation.isPending,
        isDuplicating: duplicateFlowMutation.isPending,
        isDeleting: deleteFlowMutation.isPending,
    };
}
